import { useEffect, useMemo, useState } from 'react';
import { Group, Code, ActionIcon, Text, Flex, SegmentedControl, Modal, Box, Menu } from '@mantine/core';
import {
  //IconSettings, IconGauge,
  //IconBuildingStore,
  IconUserCircle,
  IconDatabaseImport,
  IconLogin,
  IconUserPlus,
  IconHome2,
  IconDeviceDesktopAnalytics,
  IconBook,
  IconMenu2,
  IconMap,
  IconApps,
  //IconMapPin,
  IconLocation,
  IconDatabaseCog,
  IconShieldLock,
  IconSettings,
  IconX,
  IconChevronDown,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SessionContextType, useSessionContext } from 'supertokens-auth-react/recipe/session';
import { signOut } from 'supertokens-auth-react/recipe/session';
import classes from './NavbarSimple.module.css';
import { useStore, UserProfile, useLanguageStore } from '../store/Store';
import { clientRoutes } from '../ApiPaths';
import { invalidateAuthenticatedProfileLoads } from '@/services/Authentication';
import { isMobile } from 'is-mobile';
import { useTranslation } from 'react-i18next';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { isCurrentUserMasterAdmin } from '@/utils/masterAdminUtils';

/** Pick the most specific static route (longest prefix) so /ecom/admin does not match /ecom. */
function resolveActiveNavLink(path: string): string {
  if (path.startsWith('/user/')) {
    return path;
  }

  const staticRoutes = Object.values(clientRoutes)
    .filter((link) => !link.includes(':') && !link.startsWith('http'))
    .sort((a, b) => b.length - a.length);

  for (const link of staticRoutes) {
    if (path === link || (link !== '/' && path.startsWith(`${link}/`))) {
      return link;
    }
  }

  return path;
}

interface NavbarSimpleProps {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  isSmallScreen?: boolean;
}

/** One top-right control while the sidebar is closed. Sign-up is the button; login sits in its menu. */
export function GuestAuthBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const session: SessionContextType = useSessionContext();
  const isLoggedIn = !session.loading && session.doesSessionExist;
  const userProfile: UserProfile = useStore((state) => state.userProfile);
  const onSignupRoute =
    location.pathname === clientRoutes.signup ||
    location.pathname.startsWith(`${clientRoutes.signup}/`);

  if (session.loading || isLoggedIn) return null;

  const navbarStyle = {
    '--navbar-active-color': userProfile.qrCodeColor,
    '--navbar-active-color-light': colorWithAlpha(userProfile.qrCodeColor, 0.1),
    '--navbar-active-color-border': colorWithAlpha(userProfile.qrCodeColor, 0.28),
  } as React.CSSProperties;

  return (
    <div className={classes.guestCorner} style={navbarStyle} data-active={onSignupRoute || undefined}>
      <Link to={clientRoutes.signup} className={classes.guestCornerSignup}>
        <IconUserPlus size={16} stroke={1.75} />
        <span>{t('navbar.signup')}</span>
      </Link>
      <Menu position="bottom-end" offset={8} shadow="md" withinPortal floatingStrategy="fixed">
        <Menu.Target>
          <button type="button" className={classes.guestCornerMore} aria-label={t('navbar.login')}>
            <IconChevronDown size={15} stroke={2.25} />
          </button>
        </Menu.Target>
        <Menu.Dropdown className={classes.guestCornerMenu}>
          <Menu.Item
            component={Link}
            to={clientRoutes.login}
            leftSection={<IconLogin size={16} stroke={1.75} />}
          >
            {t('navbar.login')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}

export function NavbarSimple({
  isOpen: propIsOpen,
  onToggle: propOnToggle,
  isSmallScreen: propIsSmallScreen,
}: NavbarSimpleProps = {}) {
  const { t } = useTranslation();
  const userProfile: UserProfile = useStore((state) => state.userProfile);
  const reset = useStore((state) => state.reset);
  const { language, setLanguage } = useLanguageStore();
  const location = useLocation();
  const [active, setActive] = useState(() => resolveActiveNavLink(location.pathname));
  const session: SessionContextType = useSessionContext();
  const isLoggedIn = !session.loading && session.doesSessionExist;
  const onLoginRoute =
    location.pathname === clientRoutes.login ||
    location.pathname.startsWith(`${clientRoutes.login}/`);
  const onSignupRoute =
    location.pathname === clientRoutes.signup ||
    location.pathname.startsWith(`${clientRoutes.signup}/`);
  const [showNavBar, setShowNavBar] = useState<boolean>(
    propIsOpen ?? (!isMobile() && !active.includes('/embed'))
  );
  const navigate = useNavigate();
  const isIframe = location.pathname.includes('/embed');
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    propIsSmallScreen ?? window.innerWidth < 1200
  );
  const isAuthorized = useMemo(() => isCurrentUserMasterAdmin(userProfile), [userProfile]);
  const [showAcademyModal, setShowAcademyModal] = useState(false);
  const [showProductUpdatesModal, setShowProductUpdatesModal] = useState(false);

  const navbarStyle = {
    '--navbar-active-color': userProfile.qrCodeColor,
    '--navbar-active-color-light': colorWithAlpha(userProfile.qrCodeColor, 0.1),
    '--navbar-active-color-hover': colorWithAlpha(userProfile.qrCodeColor, 0.14),
    '--navbar-active-color-border': colorWithAlpha(userProfile.qrCodeColor, 0.28),
  } as React.CSSProperties;

  // Sync with parent component's state
  useEffect(() => {
    if (propIsOpen !== undefined) {
      setShowNavBar(propIsOpen);
    }
  }, [propIsOpen]);

  useEffect(() => {
    if (propIsSmallScreen !== undefined) {
      setIsSmallScreen(propIsSmallScreen);
    }
  }, [propIsSmallScreen]);

  useEffect(() => {
    setActive(resolveActiveNavLink(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (isSmallScreen) {
      setShowNavBar(false);
      if (propOnToggle) {
        propOnToggle(false);
      }
    }
  }, [location.pathname, isSmallScreen, propOnToggle]);

  // Check screen size when it changes (only if not controlled by parent)
  useEffect(() => {
    if (propIsSmallScreen !== undefined) return; // Skip if controlled by parent

    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const smallScreen = screenWidth < 1200; // Consider as small screen if less than 1200px (includes iPad and iPad Pro)
      setIsSmallScreen(smallScreen);

      if (smallScreen) {
        setShowNavBar(false);
        if (propOnToggle) {
          propOnToggle(false);
        }
      } else {
        const shouldShow = !active.includes('/embed');
        setShowNavBar(shouldShow);
        if (propOnToggle) {
          propOnToggle(shouldShow);
        }
      }
    };

    // Check on first load
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [active, propIsSmallScreen, propOnToggle]);

  useEffect(() => {
    // The mobile nav covers the page. Keep the page still until the menu closes or the layout becomes a sidebar.
    if (!isSmallScreen || !showNavBar || isIframe) return;
    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousRootOverflow = documentElement.style.overflow;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousRootOverflow;
    };
  }, [isSmallScreen, showNavBar, isIframe]);

  if (isIframe) return null;

  const data = [
    {
      link: clientRoutes.portfolioVerse,
      label: t('navbar.portfolio'),
      icon: IconMap,
    },
    { link: clientRoutes.home, label: t('navbar.marketplace'), icon: IconHome2 },
    {
      link: clientRoutes.dashboard,
      label: t('navbar.dashboard'),
      icon: IconDeviceDesktopAnalytics,
    },
    { link: '#', label: t('navbar.academy'), icon: IconBook, isAcademy: true },
    {
      link: clientRoutes.createproduct,
      label: t('navbar.createProduct'),
      icon: IconDatabaseImport,
    },
  ];

  if (isLoggedIn && userProfile.username) {
    data.push({
      link: clientRoutes.user.replace(':username', userProfile.username),
      label: t('navbar.profile'),
      icon: IconUserCircle,
    });
  }

  const dataWithEcom = [...data];

  if (isLoggedIn) {
    dataWithEcom.push({ link: clientRoutes.ecom, label: t('navbar.app'), icon: IconApps });
    dataWithEcom.push({ link: clientRoutes.ecomBeta, label: t('navbar.appBeta'), icon: IconApps });
    dataWithEcom.push({ link: clientRoutes.ecomAdmin, label: t('navbar.appAdmin'), icon: IconDatabaseCog });

    // Only show Portfolio Admin link if user is authorized
    if (isAuthorized) {
      dataWithEcom.push({
        link: clientRoutes.masterAdmin,
        label: t('navbar.masterAdmin'),
        icon: IconSettings,
      });
    }
  }

  const links = dataWithEcom.map((item) => {
    // Special handling for Academy link
    if (item.isAcademy) {
      return (
        <button
          key="academy"
          type="button"
          className={`${classes.link} ${classes.linkButton}`}
          onClick={() => {
            setShowAcademyModal(true);
            closeNavIfSmall();
          }}
        >
          <item.icon className={classes.linkIcon} stroke={1.75} />
          <span className={classes.linkLabel}>{item.label}</span>
        </button>
      );
    }

    // Use normal React Router Link for other links
    return (
      <Link
        to={item.link}
        className={classes.link}
        data-active={item.link === active || undefined}
        key={item.link}
        onClick={() => {
          if (isSmallScreen) {
            setShowNavBar(false);
            if (propOnToggle) {
              propOnToggle(false);
            }
          }
        }}
      >
        <item.icon className={classes.linkIcon} stroke={1.75} />
        <span className={classes.linkLabel}>{item.label}</span>
      </Link>
    );
  });

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    invalidateAuthenticatedProfileLoads();
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
    reset();
    navigate(clientRoutes.login);
  };

  const closeNavIfSmall = () => {
    if (!isSmallScreen) return;
    setShowNavBar(false);
    propOnToggle?.(false);
  };

  return (
    <>
      {/* Academy Modal */}
      <Modal
        opened={showAcademyModal}
        onClose={() => setShowAcademyModal(false)}
        size="95%"
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        <Box style={{ height: '90vh', width: '100%', position: 'relative' }}>
          {/* Close button */}
          <ActionIcon
            onClick={() => setShowAcademyModal(false)}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <IconX size={20} stroke={2} />
          </ActionIcon>
          <iframe
            src="https://portfolio-rbtp.notion.site/ebd/804a7e6818b64ec7b55589957a1a5d2b"
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
            title={t('navbar.academyIframeTitle')}
            style={{ border: 'none' }}
          />
        </Box>
      </Modal>

      {/* Product Updates Modal */}
      <Modal
        opened={showProductUpdatesModal}
        onClose={() => setShowProductUpdatesModal(false)}
        size="95%"
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        <Box style={{ height: '90vh', width: '100%', position: 'relative' }}>
          {/* Close button */}
          <ActionIcon
            onClick={() => setShowProductUpdatesModal(false)}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <IconX size={20} stroke={2} />
          </ActionIcon>
          <iframe
            src={clientRoutes.patchNotes}
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
            title={t('navbar.productUpdatesIframeTitle')}
            style={{ border: 'none' }}
          />
        </Box>
      </Modal>

      {!showNavBar && (
        <ActionIcon
          style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            zIndex: 200,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
            backgroundColor: 'color-mix(in srgb, var(--mantine-color-body) 75%, transparent)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: '8px',
            padding: '4px',
            color: 'var(--mantine-color-text)',
          }}
          onClick={() => {
            setShowNavBar(true);
            if (propOnToggle) {
              propOnToggle(true);
            }
          }}
          variant="transparent"
          aria-label={t('navbar.openNavigation')}
          color={userProfile.qrCodeColor}
          size="lg"
        >
          <IconMenu2
            style={{ width: '70%', height: '70%', color: userProfile.qrCodeColor }}
            stroke={2}
          />
        </ActionIcon>
      )}
      <nav
        className={`${isSmallScreen ? classes.navbarMobile : classes.navbar} ${!showNavBar ? (isSmallScreen ? classes.navbarMobileHidden : classes.navbarHidden) : ''}`}
        style={{
          display: isIframe ? 'none' : 'flex',
          ...navbarStyle,
          overscrollBehavior: 'contain', // Prevents scroll chaining
        }}
      >
        <div className={classes.navbarMain}>
          <div className={classes.header}>
            <Group justify="space-between" align="center" wrap="nowrap">
              <ActionIcon
                onClick={() => {
                  const newState = !showNavBar;
                  setShowNavBar(newState);
                  if (propOnToggle) {
                    propOnToggle(newState);
                  }
                }}
                variant="filled"
                aria-label={t('navbar.toggleNavigation')}
                color={userProfile.qrCodeColor}
                size="md"
              >
                <IconMenu2 style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>

              <SegmentedControl
                value={language}
                onChange={() => setLanguage(language === 'de' ? 'en' : 'de')}
                data={[
                  { label: 'DE', value: 'de' },
                  { label: 'EN', value: 'en' },
                ]}
                size="xs"
                radius="xl"
                styles={{
                  label: {
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    transition: 'all 0.2s ease',
                  },
                  root: {
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                    transition: 'transform 0.2s ease',
                  },
                  control: {
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                    transition: 'background-color 0.2s ease',
                  },
                }}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  width: isSmallScreen ? 'auto' : 'auto',
                  minWidth: '80px',
                }}
              />
            </Group>
            <Flex direction="column" gap={0} mt={10}>
              <Text fw={700} size="md">
                {userProfile.companyName || t('navbar.myPortfolio')}
              </Text>
              <Text size="xs" c="dimmed">
                {t('navbar.poweredBy')}
              </Text>
            </Flex>
          </div>

          {links}
        </div>

        <div className={classes.footer}>
          <button
            type="button"
            className={`${classes.link} ${classes.linkButton}`}
            onClick={() => {
              setShowProductUpdatesModal(true);
              closeNavIfSmall();
            }}
          >
            <span className={classes.linkLabel}>{t('navbar.productUpdates')}</span>
          </button>
          {isAuthorized ? (
            <Link
              to={clientRoutes.partnerGuide}
              className={classes.link}
              data-active={clientRoutes.partnerGuide === active || undefined}
              onClick={() => {
                if (isSmallScreen) {
                  setShowNavBar(false);
                  if (propOnToggle) {
                    propOnToggle(false);
                  }
                }
              }}
            >
              <span className={classes.linkLabel}>{t('navbar.partnerGuide')}</span>
            </Link>
          ) : null}
          <Link
            to={clientRoutes.account}
            className={classes.link}
            data-active={clientRoutes.account === active || undefined}
            onClick={() => {
              if (isSmallScreen) {
                setShowNavBar(false);
                if (propOnToggle) {
                  propOnToggle(false);
                }
              }
            }}
          >
            <span className={classes.linkLabel}>{t('navbar.account')}</span>
          </Link>
          <Link to="https://robotop.de/" className={classes.link}>
            <span>{t('navbar.aboutUs')}</span>
          </Link>
          <Link to="https://robotop.de/impressum/" className={classes.link}>
            <span>{t('navbar.imprint')}</span>
          </Link>
          <Link to="https://robotop.de/datenschutzerklaerung/" className={classes.link}>
            <span>{t('navbar.privacy')}</span>
          </Link>
          <Link to="https://robotop.de/terms-of-service/" className={classes.link}>
            <span>{t('navbar.terms')}</span>
          </Link>
          {isLoggedIn ? (
            <a href="#" className={`${classes.link} ${classes.logoutLink}`} onClick={handleLogout}>
              <IconLogin className={classes.linkIcon} stroke={1.5} />
              <span>{t('navbar.logout')}</span>
            </a>
          ) : !session.loading ? (
            <div className={classes.footerAuth}>
              <IconLogin className={classes.linkIcon} stroke={1.5} />
              <span>
                <Link
                  to={clientRoutes.login}
                  className={classes.footerAuthLink}
                  data-active={onLoginRoute || undefined}
                  onClick={closeNavIfSmall}
                >
                  {t('navbar.login')}
                </Link>
                {' / '}
                <Link
                  to={clientRoutes.signup}
                  className={classes.footerAuthLink}
                  data-active={onSignupRoute || undefined}
                  onClick={closeNavIfSmall}
                >
                  {t('navbar.signup')}
                </Link>
              </span>
            </div>
          ) : null}
        </div>
      </nav>
    </>
  );
}
